/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useMediaQuery, useTheme } from "@mui/system";
import { useLocation, useParams } from "react-router-dom";
import { Breadcrumbs, IconButton, InputAdornment, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { startCase, capitalize } from 'lodash'
import {
  PageContainer,
  StyledBreadCrumbContainer,
  StyledLink,
} from "../../components/page_layout_components/styles";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { MenuHeading } from "../../components/menu_heading";
import { ImportLogMetric } from "../../components/import_log_metric";
import api from "../../api";
import authenticatedFetch from "../../api/authenticatedFetch";
import { buildQueryParams } from "../../utils/buildQueryParams";
import { useNotificationContext } from "../../contexts/notificationContext";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Query_Keys } from "../../lib/queryKeys";
import camelize from "../../utils/camelize";
import { IndexTable } from "../../components/index_table";
import { Loader } from "../../components/loader";
import { RowGuestView } from "../../components/guest_details_row";
import { RenderList } from "../../components/render_list";
import { StatusContainer, StyledActionContainer, StyledSearchBar, StyledSearchContainer } from "./styles";
import { useDebounce } from "../../utils/useDebounce";
import { ConfirmationDialog } from "../../components/confirmation_dialog";
import { MergeEmployeeModal } from "../../components/merge_modal";
import { CreateRecordModal } from "../../components/create_record_modal";
import { useOtMutation } from "../../hooks/mutations/useOtMutation";
import { conflictMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/conflictMutationKeys";
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from "@mui/icons-material/Check";
import MergeIcon from '@mui/icons-material/Merge';
import AddIcon from '@mui/icons-material/Add';

const CLICKABLE_CARDS = ['exact_match', 'new_created', 'total_mismatches', 'phone_mismatch', 'phone_missing', 'name_mismatch']
const MISMATCHED_TABLE_CARDS = ['total_mismatches', 'phone_mismatch', 'phone_missing', 'name_mismatch']
const EXACT_MATCH_TABLE_CARDS = ['exact_match', 'new_created']
const CONFLICT_SKIP = ['total_mismatches', 'exact_match', 'new_created']
const ACTION_BUTTON_DISABLED = ['pending']
const CREATE_BUTTON_DISABLED = ['phone_missing', 'phone_mismatch']

export const ImportLogDetailPage = () => {
  const { id } = useParams();
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState('id');
  const [exactMatchOrderBy, setExactMatchOrderBy] = useState('id')
  const [exactMatchOrder, setExactMatchOrder] = useState('desc')
  const [order, setOrder] = useState('desc');
  const [selectedCard, setSelectedCard] = useState('exact_match');
  const [resolvedSearch, setResolvedSearch] = useState('');
  const debouncedResolvedSearch = useDebounce(resolvedSearch.trim(), 300);
  const [conflictSearch, setConflictSearch] = useState('');
  const debouncedConflictSearch = useDebounce(conflictSearch.trim(), 300);
  const [discardModalOpen, setDiscardModalOpen] = useState(false)
  const [mergeOpen, setMergeOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [selectedConflictedRow, setSelectedConflictedRow] = useState(null)
  const [selectedMatch, setSelectedMatch] = useState(null)
  const [createPhoneNumber, setCreatePhoneNumber] = useState('')
  const [csvLoading, setCsvLoading] = useState(false)
  const showNotification = useNotificationContext()
  const normalizedConflictTag = !CONFLICT_SKIP.includes(selectedCard) ? selectedCard : null;
  const normalizedResolvedTag = !MISMATCHED_TABLE_CARDS.includes(selectedCard) ? selectedCard : null;
  const showConflicted = MISMATCHED_TABLE_CARDS.includes(selectedCard)
  const showResolved = EXACT_MATCH_TABLE_CARDS.includes(selectedCard)

  const fetchImportConflict = async () => {
    const res = await api.get(`/v1/admin/import_sessions/${id}/conflicts`, {params: {
      per_page: itemsPerPage,
      page: page,
      sort_by: orderBy,
      sort_order: order,
      conflict_tag: normalizedConflictTag,
      search: debouncedConflictSearch || undefined,
    }})

    return { data: res?.data, pageCount: res?.headers["total-count"] }
  }

  const { data: conflictData, isFetching: conflictFetching } = useQuery({
    queryKey: Query_Keys.import_conflict({
      per_page: itemsPerPage,
      page: page,
      sort_by: orderBy,
      sort_order: order,
      conflict_tag: normalizedConflictTag,
      search: debouncedConflictSearch,
      log_id: id,
    }),
    queryFn: fetchImportConflict,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: showConflicted
  })

  const fetchResolvedData = async () => {
    const res = await api.get(`/v1/admin/import_sessions/${id}/network_guest_details`, { params: {
      per_page: itemsPerPage,
      page: page,
      sort_by: exactMatchOrderBy,
      sort_order: exactMatchOrder,
      detail_type: normalizedResolvedTag,
      search: debouncedResolvedSearch || undefined,
    }})

    return { data: res?.data, pageCount: res?.headers["total-count"] }
  }

  const { data: resolvedData, isFetching: resolvedFetching } = useQuery({
    queryKey: Query_Keys.resolved_conflict({
      per_page: itemsPerPage,
      page: page,
      sort_by: exactMatchOrderBy,
      sort_order: exactMatchOrder,
      tag: normalizedResolvedTag,
      search: debouncedResolvedSearch,
      log_id: id,
    }),
    queryFn: fetchResolvedData,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: showResolved,
  })

  const fetchImportLogMetric = async () => {
    const res = await api.get(`/v1/admin/import_sessions/${id}/summary`)

    return res?.data
  }

  const { data: metricData, isFetching: metricFetching } = useQuery({
    queryKey: Query_Keys.import_log_metric({
      log_id: id,
    }),
    queryFn: fetchImportLogMetric,
    staleTime: 10 * 60 * 1000,
  })

  const fetchConflictedEmployee = async () => {
    const res = await api.get(`/v1/admin/employee_import_conflicts/${selectedConflictedRow?.id}`)

    return res?.data
  }

  const { data: conflictedEmployeeData, isFetching: conflictedEmployeeFetching } = useQuery({
    queryKey: Query_Keys.conflicted_employees({
      conflict_id: selectedConflictedRow?.id,
      log_id: id,
    }),
    queryFn: fetchConflictedEmployee,
    staleTime: 10 * 60 * 1000,
    enabled: (mergeOpen || createOpen) && !!selectedConflictedRow?.id,
  })

  const handleSortChange = (columnKey) => {
    const nextOrder = orderBy === columnKey ? (order === 'asc' ? 'desc' : 'asc') : 'asc'
    setOrderBy(columnKey)
    setOrder(nextOrder)
    setPage(1)
  }

  const handleExactMatchSortChange = (columnKey) => {
    const nextOrder = exactMatchOrderBy === columnKey ? (exactMatchOrder === 'asc' ? 'desc' : 'asc') : 'asc'
    setExactMatchOrderBy(columnKey)
    setExactMatchOrder(nextOrder)
    setPage(1)
  }

  const formatText = (data) => {
    return data ? startCase(data) : ''
  }

  const handleCardClick = useCallback((cardValue) => {
    if (!CLICKABLE_CARDS.includes(cardValue)) return;
    setSelectedCard(cardValue);
    setResolvedSearch('');
    setConflictSearch('');
    setPage(1)
  }, []);

  const pendingRowResetRef = useRef(null)

  const cancelPendingRowReset = () => {
    if (pendingRowResetRef.current) {
      clearTimeout(pendingRowResetRef.current)
      pendingRowResetRef.current = null
    }
  }

  useEffect(() => {
    return cancelPendingRowReset
  }, [])

  const handleResolvedSearchChange = (value) => {
    setResolvedSearch(value)
    setPage(1)
  }

  const handleConflictSearchChange = (value) => {
    setConflictSearch(value)
    setPage(1)
  }

  const handleDiscardOpen = (data) => {
    cancelPendingRowReset()
    setSelectedConflictedRow(data)
    setDiscardModalOpen(true)
  }

  const handleDiscardClose = () => {
    setSelectedConflictedRow(null)
    setDiscardModalOpen(false)
  }

  const { mutateAsync: discardConflictMutation, isPending: discardLoading } = useOtMutation({
    mutationFn: async ({ id }) =>
      await api.patch(`/v1/admin/employee_import_conflicts/${id}/discard`),
    invalidateKeys: conflictMutationKeyGroups.discard,
    successMessage: 'Record discarded successfully',
    errorMessage: 'Failed to discard the record',
    onSuccess: handleDiscardClose,
  })

  const handleDiscardConfirm = async () => {
    try {
      await discardConflictMutation({
        id: selectedConflictedRow?.id,
      })
    } catch (error) {
      // Errors are surfaced by the mutation hook.
    }
  }

  const handleMergeOpen = (data) => {
    cancelPendingRowReset()
    setSelectedConflictedRow(data)
    setMergeOpen(true)
  }

  const handleMergeClose = () => {
    setMergeOpen(false)
    setSelectedMatch(null)
    cancelPendingRowReset()
    // Allows modal to close smoothly without any flicker
    pendingRowResetRef.current = setTimeout(() => setSelectedConflictedRow(null), 500)
  }

  const handleCreateOpen = (data) => {
    cancelPendingRowReset()
    setSelectedConflictedRow(data)
    setCreateOpen(true)
  }

  const handleCreateClose = () => {
    setCreateOpen(false)
    setCreatePhoneNumber('')
    cancelPendingRowReset()
    // Allows modal to close smoothly without any flicker
    pendingRowResetRef.current = setTimeout(() => setSelectedConflictedRow(null), 500)
  }

  const { mutateAsync: mergeConflictMutation, isPending: mergeLoading } = useOtMutation({
    mutationFn: async ({ id, network_guest_id }) =>
      await api.patch(`/v1/admin/employee_import_conflicts/${id}/merge`, { network_guest_id }),
    invalidateKeys: conflictMutationKeyGroups.merge,
    successMessage: 'Record merged successfully',
    errorMessage: ({ error }) => {
      const apiErrors = error?.response?.data?.errors || {}
      return capitalize(apiErrors?.network_guest_id?.[0]) || capitalize(apiErrors?.base?.[0]) || 'Failed to merge the record'
    },
    onSuccess: handleMergeClose,
  })

  const handleMergeConfirm = async () => {
    try {
      await mergeConflictMutation({
        id: selectedConflictedRow?.id,
        network_guest_id: selectedMatch?.id,
      })
    } catch (error) {
      // Errors are surfaced by the mutation hook.
    }
  }

  const { mutateAsync: createConflictMutation, isPending: createLoading } = useOtMutation({
    mutationFn: async ({ id, phone_number }) =>
      await api.patch(`/v1/admin/employee_import_conflicts/${id}/create_guest`, { phone_number }),
    invalidateKeys: conflictMutationKeyGroups.create,
    successMessage: 'Record created successfully',
    errorMessage: ({ error }) => {
      const apiErrors = error?.response?.data?.errors || {}
      return capitalize(apiErrors?.phone_number?.[0]) || capitalize(apiErrors?.base?.[0]) || 'Failed to create a record'
    },
    onSuccess: handleCreateClose,
  })

  const handleCreateConfirm = async () => {
    try {
      await createConflictMutation({
        id: selectedConflictedRow?.id,
        phone_number: conflictedEmployeeData?.conflict_phone ? undefined : createPhoneNumber,
      })
    } catch (error) {
      // Errors are surfaced by the mutation hook.
    }
  }
  
  const handleExportCsv = async () => {
    try {
      setCsvLoading(true)
      const query = buildQueryParams({ session_log_id: id, conflict_tag: normalizedConflictTag, search: debouncedConflictSearch || undefined })
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=employee_import_conflicts&${query}`

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      })

      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = downloadUrl
      const filename = `import_log_conflicts_${normalizedConflictTag || 'all'}_${dayjs(new Date()).format('YYYY-MM-DD')}.csv`

      a.download = filename
      a.click()

      window.URL.revokeObjectURL(downloadUrl)

      showNotification('CSV downloaded successfully', 'success')
    } catch (error) {
      console.error('CSV download error:', error)
      showNotification(error?.message || 'Failed to download CSV', 'error')
    } finally {
      setCsvLoading(false)
    }
  }

  const tableColumns = [
    { key: "id", sortable: true },
    { key: "incoming_record", sortable: true },
    { key: "existing_record", sortable: true },
    { key: "existing_restaurants", sortable: false },
    { key: "status", sortable: true },
    { key: "conflict_tag", sortable: true },
    { key: "action", sortable: true }
  ];

  const columns = tableColumns?.map((column) => {
    return {
      key: column.key,
      label: camelize(column.key),
      sortable: column.sortable,
    };
  });

  const exactMatchTableColumns = [
    { key: "id", sortable: true },
    { key: "incoming_record", sortable: true },
    { key: "existing_record", sortable: true },
    { key: "existing_restaurants", sortable: false },
    { key: "tag", sortable: false }
  ]

  const exactMatchColumns = exactMatchTableColumns?.map((column) => {
    return {
      key: column.key,
      label: camelize(column.key),
      sortable: column.sortable,
    };
  });

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        id: record?.id,
        incoming_record: (
          <RowGuestView 
            data={record?.incoming_record}
            showEmail={false}
          />
        ),
        existing_record: (
          (record?.existing_record || []).map((data, index) => (
            <div key={index}>
              <RowGuestView
                data={data}
                showEmail={true}
                index={index + 1}
                totalCount={record?.existing_record?.length || 0}
              />
            </div>
          ))
        ),
        existing_restaurants: (
          <RenderList 
            data={record?.existing_restaurants?.map((data) => data?.name)}
          />
        ),
        status: (
          <StatusContainer action={record?.status}>{formatText(record?.status)}</StatusContainer>
        ),
        conflict_tag: (
          <StatusContainer action={'conflict_tag'}>{formatText(record?.conflict_tag)}</StatusContainer>
        ),
        action: (
          <StyledActionContainer>
            <Tooltip title="Create" arrow>
              <IconButton
                data-testid='create-btn'
                onClick={() => handleCreateOpen(record)}
                disabled={!(ACTION_BUTTON_DISABLED.includes(record?.status) && CREATE_BUTTON_DISABLED.includes(record?.conflict_tag))}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Merge" arrow>
              <IconButton
                data-testid='merge-btn'
                onClick={() => handleMergeOpen(record)}
                disabled={!ACTION_BUTTON_DISABLED.includes(record?.status)}
              >
                <MergeIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title='Discard' arrow>
              <IconButton
                data-testid='discard-btn'
                onClick={() => handleDiscardOpen(record)}
                disabled={!ACTION_BUTTON_DISABLED.includes(record?.status)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </StyledActionContainer>
        )
      };
    });
  };

  const rows = TableRows(conflictData?.data);

  const ExactMatchTableRows = (data) => {
    return data?.map((record) => {
      return {
        id: record?.id,
        incoming_record: (
          <RowGuestView 
            data={record?.incoming_record}
            showEmail={true}
          />
        ),
        existing_record: (
          <RowGuestView 
            data={record?.existing_record}
            showEmail={true}

          />
        ),
        existing_restaurants: (
          <RenderList 
            data={record?.existing_restaurants?.map((data) => data?.name)}
          />
        ),
        tag: (
          <StatusContainer action={'conflict_tag'}>{formatText(record?.tag)}</StatusContainer>
        ),
      };
    });
  };

  const exactMatchRows = ExactMatchTableRows(resolvedData?.data)

  return (
    <PageContainer>
      {/* Navigation Section */}
      <StyledBreadCrumbContainer>
        <Breadcrumbs data-testid='breadcrumb-section'>
          <NavigableBreadcrumbLink
            LinkComponent={StyledLink} 
            label="Home" 
            to="/admin/dashboard"
            dataTestId='home-breadcrumb-option'
          />
          <StyledLink color={'#515152'}>
            Logs
          </StyledLink>
          <NavigableBreadcrumbLink
            LinkComponent={StyledLink} 
            label="Import Logs" 
            to="/admin/import_logs"
            dataTestId='import-log-breadcrumb-option'
            state={{
              restaurant_group_id: location?.state?.restaurant_group_id,
              restaurant_ids: location?.state?.restaurant_ids,
              start_date: location?.state?.start_date || null,
              end_date: location?.state?.end_date || null,
            }}
          />
          <StyledLink color={'#515152'}>
            Import Log Details
          </StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>
      
      <MenuHeading report={false}>Import Log Details</MenuHeading>

      <ImportLogMetric
        isMobile={isMobile}
        data={metricData}
        loading={metricFetching}
        CLICKABLE_CARDS={CLICKABLE_CARDS}
        selectedCard={selectedCard}
        handleCardClick={handleCardClick}
      />

      {
        showConflicted && (
          <StyledSearchContainer>
            <StyledSearchBar
              value={conflictSearch}
              placeholder="Search by name or phone"
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
              }}
              onChange={(e) => handleConflictSearchChange(e.target.value)}
              autoComplete="off"
              disabled={!conflictSearch && (conflictData?.data?.length ?? 0) === 0}
              inputProps={{ 'data-testid': 'conflict-search-input' }}
            />
          </StyledSearchContainer>
        )
      }

      {
        conflictFetching && showConflicted ? (
          <Loader />
        ) : ( showConflicted &&
          <IndexTable
            columns={columns}
            data={rows}
            tableId="import-log-details-entries"
            type="entries"
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={conflictData?.pageCount}
            report={false}
            showDownloadButton={true}
            disableDownloadButton={rows?.length === 0 || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
            serverSort={true}
            onSortChange={handleSortChange}
            serverOrderBy={orderBy}
            serverOrder={order}
          />
        )
      }

      {
        showResolved && (
          <StyledSearchContainer>
            <StyledSearchBar
              value={resolvedSearch}
              placeholder="Search by name, email or phone"
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
              }}
              onChange={(e) => handleResolvedSearchChange(e.target.value)}
              autoComplete="off"
              disabled={!resolvedSearch && (resolvedData?.data?.length ?? 0) === 0}
              inputProps={{ 'data-testid': 'resolved-search-input' }}
            />
          </StyledSearchContainer>
        )
      }

      {
        resolvedFetching && showResolved ? (
          <Loader />
        ) : (
          showResolved && (
            <IndexTable
            columns={exactMatchColumns}
            data={exactMatchRows}
            tableId="import-log-details-exact-match-entries"
            type="entries"
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={resolvedData?.pageCount}
            report={false}
            showDownloadButton={false}
            serverSort={true}
            onSortChange={handleExactMatchSortChange}
            serverOrderBy={exactMatchOrderBy}
            serverOrder={exactMatchOrder}
          />
          )
        )
      }

      {/* Discard Modal */}
      <ConfirmationDialog
        open={discardModalOpen}
        onClose={handleDiscardClose}
        Icon={CheckIcon}
        dialogTitle="Are you sure you want to discard this record?"
        dialogBody="Selected record will be discarded"
        confirmationText="Yes, I want to discard this record"
        handleConfirm={handleDiscardConfirm}
        isLoading={discardLoading}
      />

      {/* Merge Modal */}
      <MergeEmployeeModal
        open={mergeOpen}
        handleClose={handleMergeClose}
        data={conflictedEmployeeData}
        selectedMatch={selectedMatch}
        setSelectedMatch={setSelectedMatch}
        isLoading={conflictedEmployeeFetching}
        handleMerge={handleMergeConfirm}
        isMerging={mergeLoading}
      />

      {/* Create Modal */}
      <CreateRecordModal
        open={createOpen}
        handleClose={handleCreateClose}
        data={conflictedEmployeeData}
        isLoading={conflictedEmployeeFetching}
        phoneNumber={createPhoneNumber}
        setPhoneNumber={setCreatePhoneNumber}
        handleCreate={handleCreateConfirm}
        isCreating={createLoading}
      />
    </PageContainer>
  )
}
